import cx from 'classnames';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ReactComponent as ChevronDown } from '../assets/chevron-down.svg';
import { ReactComponent as GoDaddyLogo } from '../assets/logo-godaddy.svg';
import { ReactComponent as NamecheapLogo } from '../assets/logo-namecheap.svg';
import sedoLogo from '../assets/logo-sedo.png';
import Button from './Button';
import {
  Layout,
  LayoutBottom,
  LayoutTop,
  LayoutHeading,
  LayoutSubheading,
} from './Layout';
import usePageData from '../lib/usePageData';
import NewsTicker from './NewsTicker';
import PoweredByNamecheap from './PoweredByNamecheap';
import SimilarDomains from './SimilarDomains';
import TrustBlocks from './TrustBlocks';
import OrDivider from './OrDivider';

function formatPrice(int) {
  return <>$&nbsp;{int.toLocaleString()}</>;
}

const BuyLink = props => (
  <a
    className={cx(
      'relative group flex items-center justify-between h-16 sm:h-20 pl-6 sm:pl-10 bg-white rounded-full',
      props.className,
    )}
    href={`/link.php?to=${window.btoa(props.href)}`}>
    {props.children}
    <Button
      arrowRight
      className={cx(
        'h-12 sm:h-16 mx-3 sm:mx-2 md:group-hover:pl-16',
        'text-lg sm:text-xl transition-spacing duration-100 ease',
        props.buttonBgClassName,
      )}>
      <span className="flex items-baseline">
        <span className="hidden md:group-hover:block w-0 mr-3 uppercase transform -translate-x-10 text-base">
          Buy
        </span>
        {props.price}
      </span>
    </Button>
  </a>
);

export default function BuyItNowPage() {
  const { config, domain } = usePageData();
  const [showAllOptions, setShowAllOptions] = useState(false);
  const options = [
    [
      config.prices.namecheap,
      <NamecheapLogo className="h-8" />,
      `https://www.namecheap.com/afternic-cart-proxy/app/index.html?domain=${domain}`,
    ],
    config.godaddyMiid
      ? [
          config.prices.godaddy,
          <GoDaddyLogo className="h-8" />,
          `https://auctions.godaddy.com/trpItemListing.aspx?miid=${config.godaddyMiid}`,
        ]
      : [],
    [
      config.prices.sedo,
      <img alt="Sedo logo" className="h-10" src={sedoLogo} />,
      `https://sedo.com/search/details/?origin=partner&partnerid=322307&domain=${domain}`,
    ],
  ]
    .filter(([price]) => !!price)
    .sort((a, b) => a[0] - b[0]);
  return (
    <Layout>
      <PoweredByNamecheap />
      <LayoutTop>
        <LayoutHeading className="mt-8 md:mt-16" variant="domain">
          {domain}
        </LayoutHeading>
        <LayoutSubheading className="mt-2 mb-24 mx-4" variant="domain">
          <FormattedMessage
            defaultMessage="<b>is available from</b> {price}"
            id="BIN_SUBHEADING"
            values={{
              b: chunks => chunks,
              price: (
                <span className="inline-block sm:mt-0 px-8 py-1 ml-2 bg-gray-475 text-black font-semibold rounded-full">
                  {formatPrice(options[0][0])}
                </span>
              ),
            }}
          />
        </LayoutSubheading>
      </LayoutTop>
      <LayoutBottom className="pb-12">
        <div className="relative max-w-xl mx-auto -mt-10 px-4 md:px-6 text-left">
          {options.length > 1 && (
            <div
              className="absolute left-0 w-full px-8 sm:px-10 -mt-8 flex justify-between items-start"
              style={{ top: '2px' }}>
              <div
                className="inline-flex items-center px-4 py-2 text-xs text-gray-200 uppercase tracking-widest bg-gray-800 rounded-full"
                style={{ top: '2px' }}>
                <span className="absolute animate-ping bg-green w-2 h-2 rounded-full"></span>
                <span className="bg-green w-2 h-2 mr-2 rounded-full"></span>
                Best price
              </div>
              {!showAllOptions && (
                <button
                  className="text-sm p-1"
                  onClick={() => setShowAllOptions(value => !value)}>
                  Show {options.length} prices{' '}
                  <ChevronDown className="w-4 h-4 inline-block" />
                </button>
              )}
            </div>
          )}
          {showAllOptions ? (
            options.map((option, index) => (
              <BuyLink
                className={index > 0 ? 'mt-3' : ''}
                buttonBgClassName={index > 0 ? 'bg-blue-alt' : 'bg-blue'}
                index={index}
                href={option[2]}
                key={index}
                price={option[0] && formatPrice(option[0])}>
                {option[1]}
              </BuyLink>
            ))
          ) : (
            <BuyLink
              buttonBgClassName="bg-blue"
              href={options[0][2]}
              price={formatPrice(options[0][0])}>
              <span className="leading-snug">
                <span className="block font-display font-semibold text-lg md:text-xl">
                  Buy It Now
                </span>
                <span className="block text-sm opacity-50">via Namecheap</span>
              </span>
            </BuyLink>
          )}
          <div className="overflow-hidden px-6 py-3">
            <OrDivider
              bgClassName="bg-gray-450"
              borderClassName="border-gray-500"
              borderWidth="3px"
              orientation="vertical"
            />
          </div>
          <BuyLink
            buttonBgClassName="bg-yellow"
            href={`https://dan.com/buy-domain/${domain}`}
            price={
              <>
                {formatPrice(Math.ceil((options[0][0] / 36) * 1.2))}
                <span className="mx-1 opacity-50">/</span>
                <span className="md:hidden">mo</span>
                <span className="hidden md:inline">month</span>
              </>
            }>
            <span className="leading-snug">
              <span className="block font-display font-semibold text-lg md:text-xl">
                <span className="hidden sm:inline">In </span>Installments
              </span>
              <span className="block text-sm opacity-50">
                Paid over 36 months
              </span>
            </span>
          </BuyLink>
        </div>
        <div
          className="flex justify-center mt-8 md:mt-20 pb-8 mx-auto"
          style={{ maxWidth: '700px' }}>
          <TrustBlocks
            blocks={[
              TrustBlocks.blockDefs.buyerProtection,
              TrustBlocks.blockDefs.safeTransfer,
              TrustBlocks.blockDefs.secureAlt,
            ]}
          />
        </div>
        <SimilarDomains className="mb-12" />
        <NewsTicker />
      </LayoutBottom>
    </Layout>
  );
}
